import { Eye as Visibility, EyeOff as VisibilityOff } from 'lucide-react';
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import CheckboxGroup from "../../components/CustomFields/CheckboxGroup";
import Dropdown from "../../components/CustomFields/Dropdown";
import InputField from "../../components/CustomFields/InputField";
import Tooltip from "./Tooltip";
import { setSelectedBranch } from '../../../../../api/action/company';
import IndianDatePickerWithIcon from '../../../../Profile/ProfileSection/IndianDatePicker';

const PersonalInfo = ({
  applicant,
  handleChange,
  handleNestedChange,
  validProducts,
  edit,
  errors,
}) => {
  const company = useSelector((state) => state.companyReducer.company);
  const dispatch = useDispatch();
  const [otherCommunity, setOtherCommunity] = useState("");
  const [showOtherCommunityInput, setShowOtherCommunityInput] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedRegistrationClass, setSelectedRegistrationClass] = useState(
    applicant.dynamicAttributes.find(
      (attr) => attr.name === "class"
    )?.value || ""
  );

  const [selectedCommunities, setSelectedCommunities] = useState(() => {
    const communityAttr = applicant.dynamicAttributes.find(
      (attr) => attr.name === "Community"
    );
    return communityAttr && communityAttr.value ? communityAttr.value.split(",") : [];
  });



  useEffect(() => {
    const registrationClass = applicant.dynamicAttributes.find(
      (attr) => attr.name === "class"
    )?.value || "";
    setSelectedRegistrationClass(registrationClass);
  }, [applicant.dynamicAttributes]);

  useEffect(() => {
    const hasOthers = selectedCommunities.some(
      (community) => !["General", "OBC", "SC", "MBC", "DNC", "Others"].includes(community)
    );
    if (hasOthers) {
      const otherVal = selectedCommunities.find(
        (c) => !["General", "OBC", "SC", "MBC", "DNC"].includes(c)
      );
      setSelectedCommunities((prev) => [...prev.filter(c => c !== otherVal), "Others"]);
      setOtherCommunity(otherVal);
      setShowOtherCommunityInput(false);
    }
  }, [selectedCommunities]);


  const handleCommunityChange = (selectedValues) => {
    const hasOthers = selectedValues.includes("Others");
    setShowOtherCommunityInput(hasOthers);

    setSelectedCommunities(selectedValues);
    handleNestedChange(
      "dynamicAttributes",
      applicant.dynamicAttributes.findIndex((attr) => attr.name === "Community"),
      "value",
      selectedValues.join(",")
    );
  };



  const [branchSelect, setBranchSelect] = useState(() => {
    const branchAttribute = applicant.dynamicAttributes.find(
      (attr) => attr.name === "Branch"
    )?.value;
    return branchAttribute || "";
  });

  const branches = company?.branches?.map((branch) => branch.name) || [];

  const handleBranchChange = (e) => {
    const selectedValue = e.target.value || "";
    setBranchSelect(selectedValue);

    if (!selectedValue) {
      dispatch(setSelectedBranch(null));
      // Clear from localStorage when no branch is selected
      localStorage.removeItem("selectedBranchPrimaryId");
      localStorage.removeItem("selectedBranch");
    }

    const selectedBranchObj = company?.branches?.find(
      (branch) => branch.name === selectedValue
    );

    if (selectedBranchObj) {
      dispatch(setSelectedBranch(selectedBranchObj));

      // Store both primary ID and full branch object in localStorage
      localStorage.setItem("selectedBranchPrimaryId", selectedBranchObj.primaryId);
      localStorage.setItem("selectedBranch", JSON.stringify(selectedBranchObj));
    }

    const branchIndex = applicant.dynamicAttributes.findIndex(
      (attr) => attr.name === "Branch"
    );

    if (branchIndex !== -1) {
      handleNestedChange(
        "dynamicAttributes",
        branchIndex,
        "value",
        selectedValue
      );
    }
  };


  return (
    <section className="">
      <h2 className="text-lg font-bold mb-4 text-center">
        Student Information
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* First Name */}
        <InputField
          id="firstName"
          label="First Name *"
          value={applicant.firstName}
          onChange={(e) => handleChange("firstName", e.target.value)}
          error={errors.firstName}
        />

        {/* Last Name */}
        <InputField
          id="lastName"
          label="Last Name *"
          value={applicant.lastName}
          onChange={(e) => handleChange("lastName", e.target.value)}
          error={errors.lastName}
        />

        {/* Email */}
        <InputField
          id="email"
          label="Email *"
          value={applicant.email}
          onChange={(e) => handleChange("email", e.target.value)}
          type="email"
          disabled={edit}
          error={errors.email}
        />

        <InputField
          id="phoneNumber"
          label="Mobile Number *"
          value={applicant.phoneNumber}
          autoComplete="new-password"
          inputProps={{ autoComplete: "off" }}
          onChange={(e) => {
            const inputValue = e.target.value.replace(/[^\d]/g, "");
            const maxLength = 10;
            if (inputValue.startsWith("91")) {
              const numberPart = inputValue.slice(2, 12);
              const formattedValue = `+91 ${numberPart}`;
              handleChange("phoneNumber", formattedValue);
            } else {
              const numberPart = inputValue.slice(0, maxLength);
              const formattedValue = `+91 ${numberPart}`;
              handleChange("phoneNumber", formattedValue);
            }
          }}
          error={errors.phoneNumber}
        />

        {!edit && (
          <div className="relative">
            <InputField
              id="password"
              label="Password *"
              value={applicant.password}
              onChange={(e) => handleChange("password", e.target.value)}
              //placeholder="Enter your Password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              inputProps={{ autoComplete: "off" }}
              error={errors.password}
            />
            <button
              type="button"
              className="absolute right-2 top-1/4 transform -translate-y-1/2 p-1 rounded-full focus:outline-none"
              onClick={() => setShowPassword(!showPassword)}
              style={{ paddingTop: '64px' }}
            >
              {showPassword ? (
                <VisibilityOff className="text-gray-500" />
              ) : (
                <Visibility className="text-gray-500" />
              )}
            </button>
          </div>
        )}
        {!edit && (
          <div className="relative">
            <InputField
              id="confirmPassword"
              label="Confirm Password *"
              value={applicant.confirmPassword}
              onChange={(e) => handleChange("confirmPassword", e.target.value)}
              type={showConfirmPassword ? "text" : "password"}
              error={errors.confirmPassword}
            />
            <button
              type="button"
              className="absolute right-2 top-1/4 transform -translate-y-1/2 p-1 rounded-full focus:outline-none"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{ paddingTop: '64px' }}
            >
              {showConfirmPassword ? (
                <VisibilityOff className="text-gray-500" />
              ) : (
                <Visibility className="text-gray-500" />
              )}
            </button>
          </div>
        )}

        <Dropdown
          id="gender"
          label="Gender *"
          value={applicant.gender} // Expecting "MALE" or "FEMALE"
          onChange={(e) => {
            const genderValue = e.target.value;
            handleChange("gender", genderValue);
          }}
          options={[
            { label: "Male", value: "MALE" },
            { label: "Female", value: "FEMALE" }
          ]}
          defaultOption="Select Gender *"
          error={errors.gender}
        />
        <IndianDatePickerWithIcon
          id="dob"
          name="dob"
          label="Date of Birth *"
          value={applicant.dob ? applicant.dob.split('T')[0] : ""}
          onChange={(e) => handleChange("dob", e.target.value)}
          error={errors.dob}
        />

        {applicant.dynamicAttributes.map((attribute, index) => {
          if (attribute.name === "class") {
            return (
              <div>
                <Dropdown
                  key={index}
                  id="registrationClass"
                  label="Class *"
                  value={selectedRegistrationClass}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    const selectedProduct = validProducts.find(
                      (product) => product.product.name === selectedValue
                    );

                    if (selectedProduct) {
                      handleNestedChange(
                        "dynamicAttributes",
                        applicant.dynamicAttributes.findIndex(
                          (attr) => attr.name === "class"
                        ),
                        "value",
                        selectedValue
                      );

                      localStorage.setItem(
                        "selectedRegistrationClass",
                        JSON.stringify(selectedProduct)
                      );
                      setSelectedRegistrationClass(selectedValue);
                    }
                  }}
                  options={
                    validProducts.length > 0
                      ? validProducts.map((item) => item.product.name)
                      : []
                  }
                  defaultOption={
                    validProducts.length > 0 ? "Select class" : "No class available"
                  }
                />
                {errors.registrationClass && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.registrationClass}
                  </p>
                )}

                {errors.registrationClass && (
                  <p className="text-red-500 text-sm mt-1">{errors.registrationClass}</p>
                )}
              </div>
            );
          } else if (attribute.name === "Community") {
            return (
              <div key={index}>
                <CheckboxGroup
                  id="community"
                  label="Community"
                  value={selectedCommunities}
                  onChange={handleCommunityChange}
                  options={["General", "OBC", "SC", "MBC", "DNC", "Others"]}
                />
                {showOtherCommunityInput && (
                  <InputField
                    id="otherCommunity"
                    label="Specify Other Community"
                    value={otherCommunity}
                    onChange={(e) => {
                      setOtherCommunity(e.target.value);
                      const updatedCommunities = [...selectedCommunities];
                      updatedCommunities[updatedCommunities.indexOf("Others")] = e.target.value;
                      handleNestedChange(
                        "dynamicAttributes",
                        applicant.dynamicAttributes.findIndex((attr) => attr.name === "Community"),
                        "value",
                        updatedCommunities.join(",")
                      );
                    }}
                  />
                )}
              </div>
            );
          } else if (attribute.name === "Stream") {
            return (
              <></>
            );
          } else if (attribute.name === "Language") {
            return (
              <div key={index} className="relative">
                <div className="flex items-center">
                  <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                    Language
                  </label>
                  &nbsp;
                  <Tooltip text="Language spoken at home." />
                </div>
                <InputField
                  id="language"
                  value={attribute.value}
                  onChange={(e) =>
                    handleNestedChange(
                      "dynamicAttributes",
                      index,
                      "value",
                      e.target.value
                    )
                  }
                />
              </div>
            );
          } else if (attribute.name === "Branch") {
            return (
              <div key={index} className="relative">
                <div key={index} className="relative">
                  <div className="flex items-center">
                    <label htmlFor="branchDropdown" className="block text-sm font-medium text-gray-700">
                      Branch
                    </label>
                    &nbsp;
                    <Tooltip text="Select the branch for the applicant." />
                  </div>
                  <Dropdown
                    id="branch"
                    value={branchSelect}
                    onChange={handleBranchChange}
                    options={branches}
                    defaultOption="Select Branch"
                  />
                </div>
              </div>
            );
          } else {
            return (
              <InputField
                key={index}
                id={`dynamicAttribute-${index}`}
                label={attribute.name}
                value={attribute.value}
                onChange={(e) =>
                  handleNestedChange(
                    "dynamicAttributes",
                    index,
                    "value",
                    e.target.value
                  )
                }
              />
            );
          }
        })}
      </div>
    </section>
  );
};

export default PersonalInfo;

